/**
 * Copyright 2025 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 */

import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Drawer } from '@arco-design/web-react';
import Conversation from './Conversation';
import ToolBar from './ToolBar';
import CameraArea from './CameraArea';
import AudioController from './AudioController';
import ConversationList from './ConversationList';
import EmotionAnalysis from '@/pages/MainPage/Menu/components/EmotionAnalysis';
import { isMobile } from '@/utils/utils';
import style from './index.module.less';
import AiAvatarCard from '@/components/AiAvatarCard';
import { RootState } from '@/store';
import UserTag from '@/components/UserTag';
import FullScreenCard from '@/components/FullScreenCard';
import { useScene } from '@/lib/useCommon';

function Room() {
  const room = useSelector((state: RootState) => state.room);
  const auth = useSelector((state: RootState) => state.auth);
  const { isShowSubtitle, scene, isFullScreen, isJoined } = room;
  const { isAvatarScene } = useScene();
  
  // 移动端情绪分析抽屉状态
  const [emotionDrawerVisible, setEmotionDrawerVisible] = useState(false);
  // 情绪分析角标计数（未读的 SSE 推送消息数）
  const [emotionBadgeCount, setEmotionBadgeCount] = useState(0);
  
  // 监听 SSE 推送的情绪分析数据，更新角标计数
  useEffect(() => {
    const handleEmotionUpdate = () => {
      // 只在抽屉关闭时增加角标计数
      if (!emotionDrawerVisible) {
        setEmotionBadgeCount(prev => prev + 1);
      }
    };
    
    window.addEventListener('emotionDataUpdate', handleEmotionUpdate);
    return () => {
      window.removeEventListener('emotionDataUpdate', handleEmotionUpdate);
    };
  }, [emotionDrawerVisible]);
  
  // 打开情绪分析抽屉
  const handleOpenEmotionDrawer = () => {
    setEmotionDrawerVisible(true);
    setEmotionBadgeCount(0); // 清除角标
  };
  
  // 移动端布局
  if (isMobile()) {
    return (
      <div className={`${style.wrapper} ${style.mobile}`}>
        {isJoined ? (
          <>
            <div className={style.mobilePlayer} id="mobile-local-player" />
            {/* 移除字幕功能 */}
            {isShowSubtitle ? null : (
              <AiAvatarCard showUserTag showStatus />
            )}
            {/* 聊天内容区域 - 全屏展示 */}
            <Conversation className={`${style.conversation} ${style.mobileConversation}`} showSubtitle={isShowSubtitle} />
            
            {/* 底部控制区域 */}
            <div className={style.mobileBottomArea}>
              <div className={style.declare}>AI生成内容由大模型生成，不能完全保障真实</div>
              <AudioController className={style.controller} />
            </div>
            
            {/* 移动端麦克风和挂断按钮 - 固定在右下角 */}
            <div className={style.mobileToolBarWrapper}>
              <ToolBar className={style.mobileToolBar} />
            </div>
            
            {/* 移动端情绪分析按钮 - 只在登录后显示 */}
            {auth.isAuthenticated && (
              <button
                className={style.emotionAnalysisBtn}
                onClick={handleOpenEmotionDrawer}
              >
                📊
                {emotionBadgeCount > 0 && (
                  <span className={style.emotionBadge}>
                    {emotionBadgeCount > 99 ? '99+' : emotionBadgeCount}
                  </span>
                )}
              </button>
            )}
            
            {/* 移动端情绪分析抽屉 */}
            <Drawer
              title="情绪分析"
              visible={emotionDrawerVisible}
              placement="right"
              width="85%"
              onCancel={() => setEmotionDrawerVisible(false)}
              footer={null}
              className={style.emotionDrawer}
            >
              <EmotionAnalysis />
            </Drawer>
          </>
        ) : (
          <>
            {/* 移动端未加入房间时显示会话列表 */}
            <div className={style.mobileConversationList}>
              <ConversationList />
            </div>
          </>
        )}
      </div>
    );
  }

  // 桌面端三栏布局
  return (
    <div className={style.wrapper}>
      {/* 左侧：会话列表 */}
      <div className={style.leftSidebar}>
        <ConversationList />
      </div>

      {/* 中间：对话内容区 */}
      <div className={style.centerContent}>
        {isJoined ? (
          <>
            {/* 对话内容 */}
            <Conversation className={style.conversation} showSubtitle={isShowSubtitle} />

            {/* 底部综合工具栏 */}
            <div className={style.conversationFooter}>
              {/* 顶部主操作区 */}
              <div className={style.footerMainArea}>
                {/* 左侧：AI 信息 */}
                <div className={style.footerLeft}>
                  <div className={style.aiIcon}>💬</div>
                  <div className={style.aiInfo}>
                    <div className={style.aiName}>小派</div>
                    <div className={style.aiStatus}>在线</div>
                  </div>
                </div>

                {/* 中间：工具栏 */}
                <div className={style.footerCenter}>
                  <ToolBar className={style.footerToolBar} />
                </div>

                {/* 右侧：音频控制 */}
                <div className={style.footerRight}>
                  <AudioController className={style.footerAudioController} />
                </div>
              </div>

              {/* 底部声明 */}
              <div className={style.footerDeclare}>
                AI生成内容由大模型生成，不能完全保障真实
              </div>
            </div>

            {/* 隐藏的视频和卡片组件（保留功能但不显示） */}
            <div style={{ display: 'none' }}>
              {isShowSubtitle && <UserTag name={scene} />}
              {(isFullScreen || isAvatarScene) && <FullScreenCard />}
              {!isFullScreen && !isAvatarScene && (
                <AiAvatarCard
                  showUserTag={!isShowSubtitle}
                  showStatus={!isShowSubtitle}
                  className={isShowSubtitle ? style.subtitleAiAvatar : ''}
                />
              )}
              <CameraArea />
            </div>
          </>
        ) : (
          <div className={style.welcomeMessage}>
            <h2>欢迎使用 AbsoluteMind</h2>
            <p>点击 &ldquo;开启新会话&rdquo; 开始您的AI对话之旅</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Room;
